import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaDatabase, FaDownload, FaTrash, FaUpload, FaUndo, FaSave, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { backupAPI } from '../services/api';
import { Modal, ModalFooter } from '../components/Modals';
import TablePagination from '../components/TablePagination';

// The file download is a plain browser navigation (so a multi-GB archive
// streams straight to disk instead of being buffered into a JS blob), which
// can't carry the Authorization header — see the download-token dance below.
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TYPE_LABELS = {
  MANUAL: { label: 'Manual', className: 'badge-primary' },
  AUTO: { label: 'Automatic', className: 'badge-success' },
  UPLOADED: { label: 'Uploaded', className: 'badge-secondary' },
  PRE_RESTORE: { label: 'Before restore', className: 'badge-warning' },
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i++; }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[i]}`;
}

function formatDateTime(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
}

function describeSchedule(s) {
  if (!s?.autoEnabled) return 'Automatic backup is off.';
  const when = s.frequency === 'WEEKLY' ? `every ${WEEKDAYS[s.dayOfWeek]}`
    : s.frequency === 'MONTHLY' ? `on day ${s.dayOfMonth} of every month`
    : 'every day';
  return `Runs ${when} at ${s.time} (server time), keeping the latest ${s.retentionCount}.${s.externalPath ? ` A copy is saved to ${s.externalPath}.` : ''}`;
}

const cardStyle = { background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)' };
const labelStyle = { fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' };
const helpStyle = { fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '4px' };

const BackupRestore = () => {
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isSuperAdmin = String(currentUser?.role || '').trim().toLowerCase() === 'super admin';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [backups, setBackups] = useState([]);
  const [storage, setStorage] = useState(null);
  const [job, setJob] = useState({});
  const [restorePhrase, setRestorePhrase] = useState('RESTORE BACKUP');

  const [includeFiles, setIncludeFiles] = useState(true);
  const [note, setNote] = useState('');
  const [starting, setStarting] = useState(false);

  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [pitr, setPitr] = useState(null);
  const [savingPitr, setSavingPitr] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);

  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreFiles, setRestoreFiles] = useState(true);
  const [understood, setUnderstood] = useState(false);
  const [phraseInput, setPhraseInput] = useState('');
  const [password, setPassword] = useState('');
  const [submittingRestore, setSubmittingRestore] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [restoreOutcome, setRestoreOutcome] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const scheduleLoaded = useRef(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async ({ silent, refreshSchedule = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await backupAPI.list();
      const data = res.data?.data || {};
      setBackups(data.backups || []);
      setStorage(data.storage || null);
      setJob(data.job || {});
      setRestorePhrase(data.restorePhrase || 'RESTORE BACKUP');
      if (refreshSchedule || !scheduleLoaded.current) {
        setSchedule(data.settings);
        setPitr(data.settings?.pitr || null);
        scheduleLoaded.current = true;
      }
      setLoadError(null);
    } catch (err) {
      if (!silent) setLoadError(err.response?.data?.message || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runningJob = job?.current || null;

  // Poll while a backup is running so its row flips to Completed on its own.
  useEffect(() => {
    if (!runningJob || restoring) return undefined;
    const timer = setInterval(() => load({ silent: true }), 3000);
    return () => clearInterval(timer);
  }, [runningJob, restoring, load]);

  // Poll while a restore is running — most other requests will stall or 401
  // while the database is being swapped underneath them.
  useEffect(() => {
    if (!restoring) return undefined;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const res = await backupAPI.getJob();
        const { current, last } = res.data?.data?.job || {};
        if (stopped || current) return;
        if (last?.kind === 'RESTORE' && last.id === restoring.id) {
          setRestoreOutcome(last.status === 'COMPLETED'
            ? { ok: true, summary: last.summary }
            : { ok: false, error: last.error });
          setRestoring(null);
        }
      } catch (err) {
        if (!stopped && err.response?.status === 401) {
          setRestoreOutcome({ ok: true, summary: null });
          setRestoring(null);
        }
      }
    }, 3000);
    return () => { stopped = true; clearInterval(timer); };
  }, [restoring]);

  const startBackup = async () => {
    setStarting(true);
    try {
      await backupAPI.create({ includeFiles, note: note.trim() || undefined });
      toast.success('Backup started');
      setNote('');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start the backup');
    } finally {
      setStarting(false);
    }
  };

  const savePitr = async () => {
    setSavingPitr(true);
    try {
      const res = await backupAPI.saveSettings({ ...schedule, pitr });
      const savedSettings = res.data?.data?.settings;
      setSchedule(savedSettings);
      setPitr(savedSettings?.pitr || null);
      toast.success('PITR destination settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save the PITR settings');
    } finally {
      setSavingPitr(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const res = await backupAPI.saveSettings(schedule);
      const savedSettings = res.data?.data?.settings;
      setSchedule(savedSettings);
      toast.success(savedSettings?.autoEnabled ? 'Automatic backup schedule saved' : 'Automatic backup turned off');
      await load({ silent: true, refreshSchedule: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save the schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const download = async (record) => {
    try {
      const res = await backupAPI.downloadToken(record.id);
      const { token, fileName } = res.data?.data || {};
      const a = document.createElement('a');
      a.href = `${API_BASE_URL}/backups/${record.id}/download?token=${encodeURIComponent(token)}`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start the download');
    }
  };

  const remove = async (record) => {
    if (!window.confirm(record.deleteRequest
      ? `Approve deletion of the backup taken ${formatDateTime(record.createdAt)}? This cannot be undone.`
      : `Delete the backup taken ${formatDateTime(record.createdAt)}? This cannot be undone.`)) return;
    try {
      await backupAPI.remove(record.id);
      toast.success('Backup deleted');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete the backup');
    }
  };

  const submitDeleteRequest = async () => {
    if (deleteReason.trim().length < 5 || submittingDelete) return;
    setSubmittingDelete(true);
    try {
      await backupAPI.deleteRequest(deleteTarget.id, deleteReason.trim());
      toast.success('Deletion request sent to Super Admin for approval');
      setDeleteTarget(null);
      setDeleteReason('');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send the deletion request');
    } finally {
      setSubmittingDelete(false);
    }
  };

  const rejectDeleteRequest = async (record) => {
    try {
      await backupAPI.rejectDeleteRequest(record.id);
      toast.success('Deletion request rejected — backup kept');
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reject the request');
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.tar$/i.test(file.name)) {
      toast.error('Choose a .tar backup file created by this app');
      return;
    }
    setUploading(true);
    setUploadPercent(0);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await backupAPI.upload(form, (evt) => {
        if (evt.total) setUploadPercent(Math.round((evt.loaded / evt.total) * 100));
      });
      if (res.data?.data?.backup?.verified === false) {
        toast.warning('Backup uploaded, but it was not created by this installation — only a Super Admin can restore it.');
      } else {
        toast.success('Backup file uploaded — it is now in the list below');
      }
      await load({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openRestore = (record) => {
    setRestoreTarget(record);
    setRestoreFiles(!!record.includesFiles);
    setUnderstood(false);
    setPhraseInput('');
    setPassword('');
  };

  const canRestore = understood && phraseInput === restorePhrase && password.trim().length > 0;

  const submitRestore = async () => {
    if (!canRestore || submittingRestore) return;
    setSubmittingRestore(true);
    try {
      await backupAPI.restore(restoreTarget.id, { password, confirmPhrase: phraseInput, restoreFiles });
      setRestoring({ id: restoreTarget.id });
      setRestoreTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start the restore');
    } finally {
      setSubmittingRestore(false);
    }
  };

  // Every session token and cached record in this browser predates the
  // restored data, so the only safe way forward is a fresh sign-in.
  const finishRestore = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const lastGood = backups.find(b => b.status === 'COMPLETED' && b.type !== 'UPLOADED' && b.type !== 'PRE_RESTORE');
  const busy = !!runningJob || !!restoring;

  const [backupPage, setBackupPage] = useState(1);
  const [backupPageSize, setBackupPageSize] = useState(25);
  const backupTotalPages = Math.max(1, Math.ceil(backups.length / backupPageSize));
  useEffect(() => { if (backupPage > backupTotalPages) setBackupPage(backupTotalPages); }, [backupPage, backupTotalPages]);
  const paginatedBackups = backups.slice((backupPage - 1) * backupPageSize, backupPage * backupPageSize);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><div className="spinner"></div></div>;
  }

  if (loadError) {
    return (
      <div style={cardStyle}>
        <p style={{ color: 'var(--danger-color, #dc2626)', marginBottom: '12px' }}>Could not load backups: {loadError}</p>
        <button className="btn btn-secondary" onClick={() => load()}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 className="page-title" style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><FaDatabase /> Backup &amp; Restore</h2>
          <p className="page-subtitle">Take, schedule, download and restore full database + file backups.</p>
        </div>
      </div>

      {lastGood ? (
        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
          Last backup: {formatDateTime(lastGood.createdAt)} ({(TYPE_LABELS[lastGood.type]?.label || lastGood.type).toLowerCase()}, {lastGood.includesFiles ? 'database + files' : 'database only'}).{' '}
          {describeSchedule(schedule)}
        </div>
      ) : (
        <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
          No backup has been taken yet. Take a manual backup now, or turn on automatic backup below.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '24px' }}>
        <div style={cardStyle}>
          <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '16px' }}>Manual Backup</h3>
          <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '14px' }}>
            Every backup contains all records, settings and the complete audit trail.
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={includeFiles} onChange={(e) => setIncludeFiles(e.target.checked)} style={{ width: '18px', height: '18px' }} />
              <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Include uploaded files</span>
            </label>
            <div style={{ ...helpStyle, marginLeft: '26px' }}>
              Documents and other uploaded attachments{storage?.uploads ? ` — currently ${formatBytes(storage.uploads.bytes)} in ${storage.uploads.count.toLocaleString()} files` : ''}. Without them the backup holds records only.
            </div>
          </div>
          <input className="form-control" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
            placeholder="Note (optional) — e.g. Before annual plan upload" style={{ marginBottom: '14px' }} />
          <button className="btn btn-primary" disabled={starting || busy} onClick={startBackup} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaDatabase /> {starting ? 'Starting...' : 'Back Up Now'}
          </button>
          {runningJob && !restoring && (
            <div style={{ marginTop: '12px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
              {runningJob.kind === 'RESTORE' ? 'Restore' : 'Backup'} in progress — {runningJob.phase}
            </div>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '16px' }}>Automatic Backup</h3>
          {schedule && (
            <>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={schedule.autoEnabled} onChange={(e) => setSchedule(s => ({ ...s, autoEnabled: e.target.checked }))} style={{ width: '18px', height: '18px' }} />
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Back up automatically on a schedule</span>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                <div>
                  <label style={labelStyle}>Frequency</label>
                  <select className="form-control" value={schedule.frequency} disabled={!schedule.autoEnabled}
                    onChange={(e) => setSchedule(s => ({ ...s, frequency: e.target.value }))}>
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                  </select>
                </div>
                {schedule.frequency === 'WEEKLY' && (
                  <div>
                    <label style={labelStyle}>Day</label>
                    <select className="form-control" value={schedule.dayOfWeek} disabled={!schedule.autoEnabled}
                      onChange={(e) => setSchedule(s => ({ ...s, dayOfWeek: Number(e.target.value) }))}>
                      {WEEKDAYS.map((label, value) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </div>
                )}
                {schedule.frequency === 'MONTHLY' && (
                  <div>
                    <label style={labelStyle}>Day of month</label>
                    <input className="form-control" type="number" min={1} max={28} value={schedule.dayOfMonth} disabled={!schedule.autoEnabled}
                      onChange={(e) => setSchedule(s => ({ ...s, dayOfMonth: Number(e.target.value) }))} />
                  </div>
                )}
                <div>
                  <label style={labelStyle}>Time (server time)</label>
                  <input className="form-control" type="time" value={schedule.time} disabled={!schedule.autoEnabled}
                    onChange={(e) => setSchedule(s => ({ ...s, time: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>Backups to keep</label>
                  <input className="form-control" type="number" min={1} max={90} value={schedule.retentionCount} disabled={!schedule.autoEnabled}
                    onChange={(e) => setSchedule(s => ({ ...s, retentionCount: Number(e.target.value) }))} />
                </div>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={schedule.includeFiles} disabled={!schedule.autoEnabled}
                    onChange={(e) => setSchedule(s => ({ ...s, includeFiles: e.target.checked }))} style={{ width: '18px', height: '18px' }} />
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Include uploaded files</span>
                </label>
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={labelStyle}>Also save a copy to external folder (optional)</label>
                <input className="form-control" value={schedule.externalPath || ''} disabled={!schedule.autoEnabled} maxLength={500}
                  placeholder="e.g. D:\PrimeClean-Backups or /mnt/backup/primeclean"
                  onChange={(e) => setSchedule(s => ({ ...s, externalPath: e.target.value }))} />
                <div style={helpStyle}>A folder on the server — an external drive, second disk or mounted network share. Each automatic backup is copied there, with the same number kept.</div>
              </div>
              <div style={{ ...helpStyle, marginBottom: '14px' }}>
                Older automatic backups are removed once the limit is reached. Manual and uploaded backups are never removed automatically.
              </div>
              <button className="btn btn-primary" disabled={savingSchedule} onClick={saveSchedule} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaSave /> {savingSchedule ? 'Saving...' : 'Save Schedule'}
              </button>
            </>
          )}
        </div>
      </div>

      {pitr && (
        <div style={{ ...cardStyle, marginTop: '24px' }}>
          <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px' }}>Point-in-Time Recovery (PITR)</h3>
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <strong>Saving these details does not start PITR.</strong> This form only records where WAL archives and full backups should be written.
            An administrator still has to install pgBackRest on the PostgreSQL server and point it at these same values — see <code>ops/backup/README.md</code>.
            Until that is done there is no continuous archiving and no point-in-time restore. Write the repository passphrase down somewhere safe before saving:
            it is never shown again, and losing it makes the archive unreadable.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px 16px' }}>
            <div>
              <label style={labelStyle}>Database host platform</label>
              <select className="form-control" value={pitr.serverPlatform} onChange={(e) => setPitr(s => ({ ...s, serverPlatform: e.target.value }))}>
                <option value="LINUX">Linux server</option>
                <option value="DOCKER_LINUX">Linux Docker container</option>
                <option value="WINDOWS">Native Windows PostgreSQL</option>
              </select>
              <div style={helpStyle}>Where the PostgreSQL server actually runs — not where this app runs.</div>
            </div>
            <div>
              <label style={labelStyle}>PostgreSQL data directory (optional)</label>
              <input className="form-control" value={pitr.postgresDataPath || ''} maxLength={500}
                placeholder={pitr.serverPlatform === 'WINDOWS' ? 'e.g. C:\\Program Files\\PostgreSQL\\16\\data' : '/var/lib/postgresql/16/main'}
                onChange={(e) => setPitr(s => ({ ...s, postgresDataPath: e.target.value }))} />
              <div style={helpStyle}>For the administrator's reference — find it with <code>SHOW data_directory;</code> on that server.</div>
            </div>
            <div>
              <label style={labelStyle}>S3-compatible endpoint (blank for AWS S3)</label>
              <input className="form-control" value={pitr.endpoint || ''} maxLength={500} placeholder="https://s3.example.com"
                onChange={(e) => setPitr(s => ({ ...s, endpoint: e.target.value }))} />
              <div style={helpStyle}>Only for a non-AWS object store (Backblaze B2, Wasabi, MinIO, DigitalOcean Spaces…).</div>
            </div>
            <div>
              <label style={labelStyle}>Bucket</label>
              <input className="form-control" value={pitr.bucket || ''} maxLength={255} placeholder="company-postgres-pitr"
                onChange={(e) => setPitr(s => ({ ...s, bucket: e.target.value }))} />
              <div style={helpStyle}>Created ahead of time, dedicated to this backup and not publicly readable.</div>
            </div>
            <div>
              <label style={labelStyle}>Region</label>
              <input className="form-control" value={pitr.region || ''} maxLength={100} placeholder="us-east-1"
                onChange={(e) => setPitr(s => ({ ...s, region: e.target.value }))} />
              <div style={helpStyle}>The bucket's region code; for a non-AWS endpoint use the value that provider documents.</div>
            </div>
            <div>
              <label style={labelStyle}>Repository folder / prefix (optional)</label>
              <input className="form-control" value={pitr.prefix || ''} maxLength={500} placeholder="client-name/production"
                onChange={(e) => setPitr(s => ({ ...s, prefix: e.target.value }))} />
              <div style={helpStyle}>A path inside the bucket, when one bucket holds backups for several servers.</div>
            </div>
            <div>
              <label style={labelStyle}>S3 access key ID</label>
              <input className="form-control" type="password" autoComplete="new-password" maxLength={500}
                placeholder={pitr.accessKeyIdSet ? 'Saved — leave blank to keep current key' : 'Enter access key ID'}
                onChange={(e) => setPitr(s => ({ ...s, accessKeyId: e.target.value }))} />
              <div style={helpStyle}>Use a key scoped to this bucket only (read, write, list) — not a root key.</div>
            </div>
            <div>
              <label style={labelStyle}>S3 secret access key</label>
              <input className="form-control" type="password" autoComplete="new-password" maxLength={500}
                placeholder={pitr.secretAccessKeySet ? 'Saved — leave blank to keep current key' : 'Enter secret access key'}
                onChange={(e) => setPitr(s => ({ ...s, secretAccessKey: e.target.value }))} />
              <div style={helpStyle}>Issued with the access key ID and shown only once by the provider.</div>
            </div>
            <div>
              <label style={labelStyle}>pgBackRest repository passphrase</label>
              <input className="form-control" type="password" autoComplete="new-password" maxLength={500}
                placeholder={pitr.repoPassphraseSet ? 'Saved — leave blank to keep current passphrase' : 'Create a strong repository passphrase'}
                onChange={(e) => setPitr(s => ({ ...s, repoPassphrase: e.target.value }))} />
              <div style={helpStyle}>Encrypts the backups at rest. Generate a long random value, e.g. <code>openssl rand -base64 32</code>.</div>
            </div>
            <div>
              <label style={labelStyle}>Full backups to retain</label>
              <input className="form-control" type="number" min={1} max={90} value={pitr.retentionFull || 2}
                onChange={(e) => setPitr(s => ({ ...s, retentionFull: e.target.value }))} />
              <div style={helpStyle}>How many recent full backups pgBackRest keeps before expiring the oldest; 2–4 is typical.</div>
            </div>
          </div>
          {pitr.serverPlatform === 'WINDOWS' && (
            <div className="alert alert-danger" style={{ marginTop: '16px' }}>
              <strong>Native Windows PITR setup is not currently supported.</strong> The repository details are saved, but the pgBackRest setup scripts target Linux.
              Do not treat PITR as enabled on a native Windows PostgreSQL server.
            </div>
          )}
          <div style={{ ...helpStyle, margin: '12px 0' }}>
            Credentials are encrypted before storage and are never returned to the browser — the key and passphrase fields always start blank, and leaving one blank keeps the stored value.
            Saving does not test connectivity or start WAL archiving.
          </div>
          <button className="btn btn-primary" disabled={savingPitr} onClick={savePitr} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaSave /> {savingPitr ? 'Saving...' : 'Save PITR Destination'}
          </button>
        </div>
      )}

      <div style={{ ...cardStyle, marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <h3 style={{ fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>Backups</h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-sm btn-outline" onClick={() => load({ silent: true })} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaSyncAlt /> Refresh
            </button>
            <input ref={fileInputRef} type="file" accept=".tar" style={{ display: 'none' }} onChange={onFileChosen} />
            <button className="btn btn-sm btn-outline" disabled={uploading || busy} onClick={() => fileInputRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaUpload /> {uploading ? `Uploading ${uploadPercent}%` : 'Upload Backup File'}
            </button>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Taken</th>
                <th>Type</th>
                <th>Contains</th>
                <th>Size</th>
                <th>By</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>No backups yet</td></tr>
              )}
              {paginatedBackups.map((r) => (
                <tr key={r.id}>
                  <td>
                    {formatDateTime(r.createdAt)}
                    {r.sourceCreatedAt && (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>originally {formatDateTime(r.sourceCreatedAt)}{r.sourceClient ? ` · ${r.sourceClient}` : ''}</div>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${TYPE_LABELS[r.type]?.className || 'badge-secondary'}`}>{TYPE_LABELS[r.type]?.label || r.type}</span>
                    {r.verified === false && (
                      <span className="badge badge-danger" style={{ marginLeft: '6px' }} title="Not created by this installation, or changed after it was created. Only a Super Admin can restore it.">Unverified</span>
                    )}
                  </td>
                  <td>
                    <div>{r.includesFiles ? 'Database + uploaded files' : 'Database only'}</div>
                    {r.status === 'COMPLETED' && (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                        {(r.rowCount ?? 0).toLocaleString()} records{r.includesFiles ? ` · ${(r.fileCount ?? 0).toLocaleString()} files` : ''}
                      </div>
                    )}
                    {r.note && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{r.note}</div>}
                    {r.externalCopy && (r.externalCopy.ok
                      ? <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>Copied to {r.externalCopy.path}</div>
                      : <span className="badge badge-danger" title={r.externalCopy.error}>External copy failed</span>)}
                  </td>
                  <td>{formatBytes(r.sizeBytes)}</td>
                  <td>{r.createdBy?.name || r.createdBy?.username || 'System (scheduled)'}</td>
                  <td>
                    {r.status === 'RUNNING' && <span className="badge badge-warning">Running</span>}
                    {r.status === 'FAILED' && <span className="badge badge-danger" title={r.error || 'Failed'}>Failed</span>}
                    {r.status === 'COMPLETED' && (
                      <>
                        <span className="badge badge-success">Completed</span>
                        {r.deleteRequest && (
                          <div style={{ marginTop: '4px' }}>
                            <span className="badge badge-warning" title={`Requested by ${r.deleteRequest.by?.name || r.deleteRequest.by?.username || 'an Admin'} on ${formatDateTime(r.deleteRequest.at)}: ${r.deleteRequest.reason}`}>
                              Deletion requested
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-outline" disabled={r.status !== 'COMPLETED'} title="Download" onClick={() => download(r)}>
                        <FaDownload />
                      </button>
                      <button className="btn btn-sm btn-outline" disabled={r.status !== 'COMPLETED' || busy || !isSuperAdmin}
                        title={!isSuperAdmin ? 'Only a Super Admin can restore a backup' : 'Restore'} onClick={() => openRestore(r)}>
                        <FaUndo />
                      </button>
                      {isSuperAdmin ? (
                        <>
                          <button className="btn btn-sm btn-danger" disabled={r.status === 'RUNNING'}
                            title={r.deleteRequest ? 'Approve deletion request' : 'Delete'} onClick={() => remove(r)}>
                            <FaTrash />
                          </button>
                          {r.deleteRequest && (
                            <button className="btn btn-sm btn-outline" title="Reject deletion request (keep backup)" onClick={() => rejectDeleteRequest(r)}>
                              <FaTimes />
                            </button>
                          )}
                        </>
                      ) : (
                        <button className="btn btn-sm btn-danger" disabled={r.status === 'RUNNING' || !!r.deleteRequest}
                          title={r.deleteRequest ? 'Deletion already requested — awaiting Super Admin approval' : 'Request deletion (needs Super Admin approval)'}
                          onClick={() => { setDeleteTarget(r); setDeleteReason(''); }}>
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {backups.length > 0 && (
          <TablePagination total={backups.length} page={backupPage} pageSize={backupPageSize} onPageChange={setBackupPage} onPageSizeChange={(n) => { setBackupPageSize(n); setBackupPage(1); }} itemLabel="backups" />
        )}

        {storage && (
          <div style={{ ...helpStyle, marginTop: '12px' }}>
            Backups on this server: {formatBytes(storage.backupBytes)}
            {storage.freeBytes != null ? ` · Free disk space: ${formatBytes(storage.freeBytes)}` : ''}
            {' '}· Stored in <code>{storage.directory}</code>
          </div>
        )}
        <div className="alert alert-warning" style={{ marginTop: '12px', fontSize: 'var(--fs-sm)' }}>
          Backups kept only on this server are lost if the server itself is lost. Download a copy regularly and keep it somewhere else — a backup file contains all of your records, so store it as securely as the system itself.
        </div>
      </div>

      {restoreTarget && (
        <Modal isOpen onClose={() => { if (!submittingRestore) setRestoreTarget(null); }} title="Restore from Backup" size="md">
            <div className="alert alert-danger" style={{ marginBottom: '16px' }}>
              <strong>All current data will be replaced</strong> with the backup taken {formatDateTime(restoreTarget.sourceCreatedAt || restoreTarget.createdAt)}.
              <div style={{ marginTop: '8px' }}>
                Every user, product, record, approval, setting and the audit trail goes back to how it was in that backup. Anything entered since then is lost.
              </div>
              <div style={{ marginTop: '8px', opacity: 0.85 }}>
                A safety copy of the current data is taken first, so this restore can itself be undone. The current license is kept.
              </div>
            </div>
            {restoreTarget.verified === false && (
              <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
                This backup is unverified — it was not created by this installation, or was changed afterwards. Restore it only if you trust where it came from.
              </div>
            )}
            {restoreTarget.includesFiles && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={restoreFiles} onChange={(e) => setRestoreFiles(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <span>Also restore uploaded files</span>
                </label>
                <div style={{ ...helpStyle, marginLeft: '26px' }}>Files from the backup are put back; files uploaded since are left in place.</div>
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                <span>I understand the current data will be replaced</span>
              </label>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Type <strong>{restorePhrase}</strong> to confirm</label>
              <input className="form-control" value={phraseInput} onChange={(e) => setPhraseInput(e.target.value)} placeholder={restorePhrase} />
            </div>
            <div>
              <label style={labelStyle}>Your password</label>
              <input className="form-control" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Current password" onKeyDown={(e) => { if (e.key === 'Enter') submitRestore(); }} />
            </div>
          <ModalFooter>
            <button className="btn btn-secondary" onClick={() => setRestoreTarget(null)} disabled={submittingRestore}>Cancel</button>
            <button className="btn btn-danger" onClick={submitRestore} disabled={!canRestore || submittingRestore}>
              {submittingRestore ? 'Starting...' : 'Restore'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {deleteTarget && (
        <Modal isOpen onClose={() => { if (!submittingDelete) setDeleteTarget(null); }} title="Request Backup Deletion" size="sm">
            <div className="alert alert-info" style={{ marginBottom: '16px' }}>
              A Super Admin must approve this. The backup taken {formatDateTime(deleteTarget.createdAt)} stays in place until then.
            </div>
            <label style={labelStyle}>Reason for deletion</label>
            <textarea className="form-control" rows={3} maxLength={500} value={deleteReason}
              placeholder="e.g. Test backup taken by mistake" onChange={(e) => setDeleteReason(e.target.value)} />
          <ModalFooter>
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={submittingDelete}>Cancel</button>
            <button className="btn btn-danger" onClick={submitDeleteRequest} disabled={deleteReason.trim().length < 5 || submittingDelete}>
              {submittingDelete ? 'Sending...' : 'Send for Approval'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {restoring && (
        <Modal isOpen onClose={() => {}} title="Restore in progress" closeButton={false} resizable={false} maximizable={false} minimizable={false} closeOnBackdropClick={false} size="sm">
          <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
          <div style={{ fontWeight: 600 }}>Restoring data — keep this page open</div>
          <div style={{ marginTop: '6px', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
            The app is unavailable to everyone until this finishes. Large backups can take several minutes.
          </div>
          </div>
        </Modal>
      )}

      {restoreOutcome && (
        <Modal isOpen onClose={() => {}} title={restoreOutcome.ok ? 'Restore complete' : 'Restore failed'} closeButton={false} resizable={false} maximizable={false} minimizable={false} closeOnBackdropClick={false} size="sm">
        {restoreOutcome?.ok ? (
          <div className="alert alert-success">
            The data has been restored from the backup.
            {restoreOutcome.summary && (
              <div style={{ marginTop: '8px' }}>
                {Object.values(restoreOutcome.summary.restored || {}).reduce((n, c) => n + c, 0).toLocaleString()} records restored
                {restoreOutcome.summary.filesRestored ? ` · ${restoreOutcome.summary.filesRestored.toLocaleString()} files restored` : ''}.
              </div>
            )}
            <div style={{ marginTop: '8px' }}>Sign in again to continue. Other users will be asked to sign in again too.</div>
          </div>
        ) : (
          <div className="alert alert-danger">
            The database was not changed — current records are intact.
            <div style={{ marginTop: '8px' }}>{restoreOutcome?.error}</div>
          </div>
        )}
          <ModalFooter>
            {restoreOutcome.ok
              ? <button className="btn btn-primary" onClick={finishRestore}>Sign in again</button>
              : <button className="btn btn-secondary" onClick={() => { setRestoreOutcome(null); load({ silent: true }); }}>Close</button>}
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default BackupRestore;
